import { gql } from '@urql/core';
import { subscribe } from 'graphql';
import React from 'react';
import { useAppState } from '../../../client/providers/AppProvider';
import {
    RecruitingHubEventsFragment,
    SpinNotificationDataFragment,
    RecruitingHubFollowsFragment,
    RecruitingHubMessagesFragment,
    RecruitingHubProfileViewsFragment,
    Espin_Role_Enum,
} from '../../../generated/spin-graphql';
import { PostSummary } from '../../../types/shared.types';
import { DashboardCta } from './DashboardCta';
import { LatestNewsCard } from './LatestNewsCard';
import { NotificationsCard } from './NotificationsCard';
import { StatisticsCard } from './StatisticsCard';
import { UpcomingEventsCard } from './UpcomingEventsCard';

type Props = {
    articles: PostSummary[];
    events: RecruitingHubEventsFragment[];
    notifications: SpinNotificationDataFragment[];
    refetch: () => void;
    followers: RecruitingHubFollowsFragment[];
    deletedFollowers: RecruitingHubFollowsFragment[];
    following: RecruitingHubFollowsFragment[];
    messages: RecruitingHubMessagesFragment[];
    profileViews: RecruitingHubProfileViewsFragment[];
    conversations: number;
};

gql`
    fragment RecruitingHubEvents on espin_event {
        ...UpcomingEventsCard
    }
    fragment RecruitingHubNotifications on espin_notification {
        ...NotificationsCard
    }
    fragment RecruitingHubFollows on espin_follow {
        id
        player_user_id
    }
    fragment RecruitingHubMessages on espin_message {
        id
        player_user_id
    }
    fragment RecruitingHubProfileViews on espin_profile_view {
        id
        player_user_id
    }
`;

export const RecruitingHub: React.FC<Props> = ({
    articles,
    events,
    notifications,
    refetch,
    followers,
    deletedFollowers,
    following,
    messages,
    profileViews,
    conversations,
}) => {
    const { user, subscribed } = useAppState();
    return (
        <div className="recruiting-hub">
            <div className="hub-cta-container">
                {user?.role === Espin_Role_Enum.EspinPlayerUser && !subscribed && (
                    <DashboardCta
                        ctaUrl={'/dashboard/subscriptions'}
                        ctaBtn={'Subscribe'}
                        header={'Subscribe Today'}
                        ctaDescrip={
                            'Subscribe today to unlock the full power of the Stay Plugged In platform and instantly connect with college coaches and programs.'
                        }
                        headColor={'green'}
                        btnColor={'green'}
                    />
                )}
                <DashboardCta
                    ctaUrl={'mailto:info@staypluggedin.com'}
                    ctaBtn={'Contact'}
                    header={'Recruiting Questions?'}
                    ctaDescrip={
                        'Our Recruiting Specialists are here to help answer any questions you may have about the recruiting process. Reach out today and let us help make recruiting easier.'
                    }
                    headColor={'blue'}
                    btnColor={'blue'}
                />
            </div>
            <div className="hub-content-container">
                <div className="left">
                    <StatisticsCard
                        profileViews={profileViews.length}
                        messages={messages.length}
                        followers={followers.length - deletedFollowers.length}
                        points={profileViews.length + followers.length + following.length + conversations}
                    />
                    <NotificationsCard notifications={notifications} refetch={refetch} />
                </div>
                <div className="right">
                    <LatestNewsCard articles={articles} />
                    <UpcomingEventsCard events={events} />
                </div>
            </div>
        </div>
    );
};
